import { Fragment, lazy, useDeferredValue, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Banknote,
  Bell,
  Car,
  ClipboardCheck,
  FileChartColumn,
  HeartPulse,
  Scale,
  Ticket,
  Timer,
  TrendingUp,
  TriangleAlert,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { averageTicketCents, operationalToday } from '@ana/domain';
import { api, errorMessage } from '../api/client.ts';
import { useSession } from '../auth/session.tsx';
import {
  Bars,
  DemoBadge,
  Empty,
  ErrorBox,
  HBars,
  Kpi,
  Loading,
  PageTitle,
  StatusBadge,
} from '../components/ui.tsx';
import { OutcomeBadge, signed, type DreBudget } from '../components/budget.tsx';
import { DateInputBr } from '../components/DateInputBr.tsx';
import { MSG } from '../content/messages.ts';
import { axisBrl } from '../lib/chart.ts';
import { KIND_LABEL, brl, dateBr, dateTimeBr, hoursBr, pct, qty, ratioPct } from '../lib/format.ts';
import { useNearViewport } from '../lib/near-viewport.ts';
import { BlockBoundary } from './dashboard/BlockBoundary.tsx';
import { PerspectiveTabs, panelId, tabId } from './dashboard/PerspectiveTabs.tsx';
import {
  PERSPECTIVE_PARAM,
  allowedPerspectives,
  canSeePending,
  defaultPerspective,
  resolvePerspective,
  type PerspectiveId,
} from './dashboard/perspectives.ts';

/** Financial abbreviations used on the panel, spelled out for screen readers and on hover. */
const MoM = () => <abbr title="Variação sobre o mesmo período do mês anterior">MoM</abbr>;
const YoY = () => <abbr title="Variação sobre o mesmo período do ano anterior">YoY</abbr>;
const YTD = () => <abbr title="Acumulado do ano até a data de referência">YTD</abbr>;

interface Comparison {
  current: number;
  base: number | null;
  absoluteChange: number | null;
  percentChange: number | null;
  comparability: string;
  reason: string | null;
}
interface Dashboard {
  referenceDate: string;
  basis: string;
  generatedAt: string;
  revenue: {
    todayCents: number;
    weekCents: number;
    monthCents: number;
    ytdCents: number;
    previousMonthFullCents: number;
    sameMonthLastYearCents: number;
    provisionalMonthCents: number;
    mom: Comparison;
    yoy: Comparison;
    ytd: Comparison;
  };
  flow: {
    todayAccess: number;
    weekAccess: number;
    monthAccess: number;
    ytdAccess: number;
    monthPaying: number;
    mom: Comparison;
  };
  ticket: { dayCents: number | null; weekCents: number | null; monthCents: number | null };
  reconciliation: {
    ratioByValue: number | null;
    expectedCents: number;
    allocatedCents: number;
    divergentCents: number;
    divergentItems: number;
    pendingItems: number;
  } | null;
  cash: {
    expectedCents: number;
    countedCents: number;
    differenceCents: number;
    /** Sum of the amounts received entered in the month's movements (every payment means; DOCX §10 "recebido"). */
    receivedCents: number;
    depositedCents: number | null;
    receivableCents: number;
  };
  approvals: {
    pending: number;
    overdue: number;
    reopened: number;
    drafts: number;
    approvedDays: number;
    overdueHours: number;
  };
  alerts: { severity: string; status: string; n: number }[];
  closings: {
    from: string;
    to: string;
    expectedDays: number;
    completeDays: number;
    pendingDays: number;
    withoutMovementDays: number;
  };
  divergenceHandling: {
    averageHours: number | null;
    resolvedCount: number;
    openCount: number;
    definition: string;
  } | null;
  dre: {
    netRevenueCents: number;
    operatingResultCents: number;
    operatingMargin: number | null;
    budget: Pick<DreBudget, 'partial' | 'actualThrough' | 'grossRevenue' | 'operatingResult'> | null;
  } | null;
  health: {
    score: number | null;
    measuredWeight: number;
    totalWeight: number;
    measuredShare: number | null;
    weightsSource: 'default' | 'parameter';
    components: { key: string; weight: number; ratio: number | null; points: number | null }[];
  };
  coverage: { monthDays: number; monthCountedDays: number };
}

/** Names of the health components (DOCX §10.1), in the order the API lists them. */
const HEALTH_LABEL: Record<string, string> = {
  reconciledRatio: 'Conciliação (por valor)',
  closedOnTimeRatio: 'Fechamentos completos',
  divergenceFreeRatio: 'Dias sem divergência de caixa',
  approvalsOnTimeRatio: 'Aprovações em dia',
  integrityRatio: 'Integridade da trilha de auditoria',
  syncOkRatio: 'Sincronizações sem conflito (30 dias)',
};

function HealthPanel({ health }: { health: Dashboard['health'] }) {
  return (
    <>
      <Kpi
        label="Indicador interno (0 a 100)"
        value={health.score ?? 'N/D'}
        sub={`Componentes medidos: ${ratioPct(health.measuredShare)} do peso total. Não substitui a análise financeira.`}
        testId="kpi-health"
      />
      <div className="table-wrap" style={{ marginTop: '0.75rem' }}>
        <table data-testid="health-components">
          <caption className="small muted" style={{ textAlign: 'left' }}>
            {health.weightsSource === 'default'
              ? 'Pesos sugeridos, ainda não validados pela gestão'
              : 'Pesos definidos nos parâmetros da unidade, ainda não validados pela gestão'}{' '}
            <DemoBadge />
          </caption>
          <thead>
            <tr>
              <th scope="col">Componente</th>
              <th scope="col" className="num">
                Peso
              </th>
              <th scope="col" className="num">
                Medido
              </th>
              <th scope="col" className="num">
                Pontos
              </th>
            </tr>
          </thead>
          <tbody>
            {health.components.map((c) => (
              <tr key={c.key}>
                <td>{HEALTH_LABEL[c.key] ?? c.key}</td>
                <td className="num">{qty(c.weight)}</td>
                <td className="num">{c.ratio === null ? 'sem dados' : ratioPct(c.ratio)}</td>
                <td className="num">{c.points === null ? '—' : c.points.toFixed(1).replace('.', ',')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
interface MonthDay {
  operationalDate: string;
  status: string;
  revenueCents: number;
  accessQty: number;
  payingQty: number;
  counted: boolean;
}
interface ChannelLine {
  code: string;
  label: string;
  kind: string;
  revenueCents: number;
}

function Change({ c }: { c: Comparison }) {
  if (c.comparability === 'NO_BASE') return <span title={c.reason ?? ''}>sem base de comparação</span>;
  return (
    <span title={c.reason ?? ''}>
      {pct(c.percentChange)}{' '}
      {c.comparability === 'NOT_COMPARABLE' ? <StatusBadge status="NOT_COMPARABLE" /> : null}
    </span>
  );
}

function Section({
  title,
  icon: Icon,
  children,
  extra,
}: {
  title: string;
  icon: LucideIcon;
  children: ReactNode;
  extra?: ReactNode;
}) {
  const ready = useSecondaryReady();
  return (
    <section className="card">
      <h3 className="card-title">
        <Icon size={18} aria-hidden="true" />
        {title} {extra}
      </h3>
      {ready ? children : null}
    </section>
  );
}

/**
 * On a phone the secondary blocks sit below the first screen: their figures are drawn in a second, deferred render,
 * right after the main indicators are painted, instead of in the same long task. Wider screens draw everything at
 * once (the blocks are in view there, and filling them in later would move the page).
 */
function useSecondaryReady(): boolean {
  const [narrow] = useState(
    () => typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 820px)').matches,
  );
  return useDeferredValue(true, !narrow);
}

// The perspective blocks that read other endpoints load as their own chunks, so the panel's first screen (LCP) does
// not carry the code of perspectives the reader may never open.
const PendingBlock = lazy(() =>
  import('./dashboard/PendingBlock.tsx').then((m) => ({ default: m.PendingBlock })),
);
const QualityBlock = lazy(() =>
  import('./dashboard/QualityBlock.tsx').then((m) => ({ default: m.QualityBlock })),
);
const SystemBlock = lazy(() =>
  import('./dashboard/SystemBlock.tsx').then((m) => ({ default: m.SystemBlock })),
);

/** A group of cards under a section heading; the first group after the main indicators carries the on-demand ref. */
interface Group {
  title: string;
  cards: (ReactNode | null)[];
}

export function DashboardPage() {
  const { unitId, session } = useSession();
  const unit = session?.units.find((u) => u.id === unitId);
  const can = (p: string) => Boolean(unit?.permissions.includes(p));
  const [params, setParams] = useSearchParams();
  const allowed = allowedPerspectives(session, unit);
  const defaultId = defaultPerspective(unit?.roles, allowed);
  const perspective = resolvePerspective(params.get(PERSPECTIVE_PARAM), unit?.roles, allowed).id;
  // Executiva, Controle Financeiro and Qualidade dos Dados read the panel figures; Saúde do Sistema reads /v1/ops.
  const panelData = perspective !== null && perspective !== 'sistema';
  const withChannels = perspective === 'executiva' || perspective === 'financeiro';
  const selectPerspective = (id: PerspectiveId) => {
    const next = new URLSearchParams(params);
    next.set(PERSPECTIVE_PARAM, id);
    // Replace: switching perspective is a change of view, like a filter, not a new page in the history.
    setParams(next, { replace: true });
  };
  const [date, setDate] = useState(operationalToday(new Date()));
  const [basis, setBasis] = useState<'approved' | 'provisional'>('approved');
  const month = date.slice(0, 7);
  // Main indicators first: the charts and the payment-method breakdown are asked for only when the reader gets near
  // them (on a phone they are several screens below), so they do not compete with the first screen.
  const [secondaryRef, nearSecondary] = useNearViewport<HTMLHeadingElement>();
  const q = useQuery({
    queryKey: ['dashboard', unitId, date, basis],
    enabled: Boolean(unitId) && panelData,
    queryFn: () => api<Dashboard>(`/reports/dashboard?unitId=${unitId}&date=${date}&basis=${basis}`),
    refetchInterval: 60_000,
  });
  // Trend charts read the same server reports as the result screens (no client-side business rule).
  const monthly = useQuery({
    queryKey: ['dashboard-month', unitId, month],
    enabled: Boolean(unitId) && panelData && can('report.read') && nearSecondary,
    queryFn: () =>
      api<{ days: MonthDay[] }>(`/reports/monthly?unitId=${unitId}&month=${month}&basis=provisional`),
    // Same rhythm as the main indicators: a day approved elsewhere reaches the charts without a reload.
    refetchInterval: 60_000,
  });
  const channels = useQuery({
    queryKey: ['dashboard-channels', unitId, month, date, basis],
    enabled: Boolean(unitId) && withChannels && can('report.read') && nearSecondary,
    queryFn: () =>
      api<{ lines: ChannelLine[]; totalCents: number }>(
        `/reports/revenue-by-channel?unitId=${unitId}&from=${month}-01&to=${date}&basis=${basis}`,
      ),
    refetchInterval: 60_000,
  });
  const d = q.data;
  const alertCount = (sev: string) =>
    d?.alerts.filter((a) => a.severity === sev && a.status !== 'RESOLVED').reduce((s, a) => s + a.n, 0) ?? 0;
  const days = (monthly.data?.days ?? []).filter((x) => x.operationalDate <= date);
  const approvedOnly = basis === 'approved';
  const isApproved = (x: MonthDay) => x.status === 'APPROVED' || x.status === 'REOPEN_PENDING';
  // Every chart follows the selected basis: provisional days only appear (hollow) under "aprovados + provisórios".
  const inBasis = (x: MonthDay) => x.counted && (!approvedOnly || isApproved(x));
  const shown = days.filter(inBasis);
  const legend = (
    <div className="legend">
      <span>
        <i aria-hidden="true" /> Aprovado
      </span>
      {!approvedOnly ? (
        <span>
          <i className="provisional" aria-hidden="true" /> Provisório (fechado ou conferido, sem aprovação)
        </span>
      ) : null}
    </div>
  );
  const byKind = Object.entries(
    (channels.data?.lines ?? []).reduce<Record<string, number>>((acc, l) => {
      if (l.revenueCents > 0) acc[l.kind] = (acc[l.kind] ?? 0) + l.revenueCents;
      return acc;
    }, {}),
  )
    .map(([kind, value]) => ({ label: KIND_LABEL[kind] ?? kind, value }))
    .sort((a, b) => b.value - a.value);
  const operators = (channels.data?.lines ?? [])
    .filter((l) => l.kind === 'AUTO_CHANNEL' && l.revenueCents > 0)
    .map((l) => ({ label: l.label, value: l.revenueCents }))
    .sort((a, b) => b.value - a.value);

  const pending =
    unitId && canSeePending(unit) ? (
      <BlockBoundary key="pending">
        <PendingBlock unitId={unitId} enabled={perspective === 'sistema' || nearSecondary} />
      </BlockBoundary>
    ) : null;

  const body = (d: Dashboard): { main: boolean; groups: Group[] } => {
    const channelsCard = can('report.read') ? (
      <section className="card" key="channels">
        <h3 className="card-title">
          <Banknote size={18} aria-hidden="true" />
          Receita por meio de pagamento
        </h3>
        {channels.isPending && !channels.error ? <Loading /> : null}
        {channels.error ? (
          <ErrorBox error={errorMessage(channels.error)} retry={() => void channels.refetch()} />
        ) : null}
        {byKind.length ? (
          <HBars rows={byKind} format={brl} label="Receita por meio de pagamento" />
        ) : channels.isSuccess ? (
          <Empty>Nenhuma receita no período selecionado.</Empty>
        ) : null}
        {operators.length ? (
          <>
            <h4>Por operadora (tags e aplicativos)</h4>
            <HBars rows={operators} format={brl} label="Receita por operadora" />
          </>
        ) : null}
      </section>
    ) : null;
    const cash = (
      <Section key="cash" title="Caixa e depósitos (mês)" icon={Wallet}>
        <div className="grid">
          <Kpi variant="small" label="Caixa esperado" value={brl(d.cash.expectedCents)} />
          <Kpi variant="small" label="Caixa apurado" value={brl(d.cash.countedCents)} />
          <Kpi
            variant="small"
            label="Recebido"
            value={brl(d.cash.receivedCents)}
            sub="lançado no movimento, todos os meios"
            testId="dashboard-received"
          />
          <Kpi variant="small" label="Sobra/falta" value={brl(d.cash.differenceCents)} />
          <Kpi
            variant="small"
            label="Depositado"
            value={brl(d.cash.depositedCents)}
            to={can('finance.read') ? '/depositos' : undefined}
            drillLabel="Depósitos"
          />
          <Kpi variant="small" label="A receber (operadoras)" value={brl(d.cash.receivableCents)} />
        </div>
      </Section>
    );
    const closings = (
      <Section key="closings" title="Fechamentos e aprovações" icon={ClipboardCheck}>
        <div className="grid">
          <Kpi
            variant="small"
            label="Fechamentos completos"
            value={qty(d.closings.completeDays)}
            sub={`de ${qty(d.closings.expectedDays)} dias (${dateBr(d.closings.from)} a ${dateBr(d.closings.to)})`}
            testId="kpi-closings-complete"
          />
          <Kpi
            variant="small"
            label="Fechamentos pendentes"
            value={qty(d.closings.pendingDays)}
            sub={`${qty(d.closings.withoutMovementDays)} dia(s) sem lançamento`}
            testId="kpi-closings-pending"
            to="/movimento"
            drillLabel="Lançar ou fechar dias"
          />
          <Kpi
            variant="small"
            label="Aguardando conferência ou aprovação"
            value={qty(d.approvals.pending)}
            to="/aprovacoes"
            drillLabel="Fila de aprovações"
          />
          <Kpi
            variant="small"
            label={`Em atraso (mais de ${d.approvals.overdueHours} h)`}
            value={qty(d.approvals.overdue)}
          />
          <Kpi variant="small" label="Reabertos" value={qty(d.approvals.reopened)} />
          <Kpi variant="small" label="Rascunhos e devolvidos" value={qty(d.approvals.drafts)} />
        </div>
      </Section>
    );
    const alerts = (
      <Section key="alerts" title="Alertas" icon={Bell}>
        <div className="grid">
          <Kpi
            variant="small"
            label="Críticos"
            value={qty(alertCount('CRITICA'))}
            to="/alertas"
            drillLabel="Alertas"
          />
          <Kpi variant="small" label="Altos" value={qty(alertCount('ALTA'))} />
          <Kpi variant="small" label="Médios" value={qty(alertCount('MEDIA'))} />
          <Kpi
            variant="small"
            label="Resolvidos (30 dias)"
            value={qty(d.alerts.filter((a) => a.status === 'RESOLVED').reduce((s, a) => s + a.n, 0))}
          />
        </div>
      </Section>
    );
    const dre = (
      <Section
        key="dre"
        title="DRE do mês"
        icon={FileChartColumn}
        extra={
          <DemoBadge title="Plano de contas e deduções ainda não validados pela contabilidade (D-11)." />
        }
      >
        {d.dre ? (
          <>
            <div className="grid">
              <Kpi
                variant="small"
                label="Receita líquida"
                value={brl(d.dre.netRevenueCents)}
                to="/relatorios/dre"
                drillLabel="DRE gerencial"
              />
              <Kpi variant="small" label="Resultado operacional" value={brl(d.dre.operatingResultCents)} />
              <Kpi variant="small" label="Margem operacional" value={ratioPct(d.dre.operatingMargin)} />
              {d.dre.budget ? (
                <>
                  <Kpi
                    variant="small"
                    label="Receita × orçado"
                    value={signed(d.dre.budget.grossRevenue.varianceAbs)}
                    sub={
                      <>
                        orçado {brl(d.dre.budget.grossRevenue.budget)}
                        {d.dre.budget.partial ? ' (mês inteiro)' : ''}{' '}
                        <OutcomeBadge outcome={d.dre.budget.grossRevenue.outcome} />
                      </>
                    }
                    testId="dashboard-budget-revenue"
                  />
                  <Kpi
                    variant="small"
                    label="Resultado × orçado"
                    value={signed(d.dre.budget.operatingResult.varianceAbs)}
                    sub={
                      <>
                        orçado {brl(d.dre.budget.operatingResult.budget)}{' '}
                        <OutcomeBadge outcome={d.dre.budget.operatingResult.outcome} />
                      </>
                    }
                  />
                </>
              ) : null}
            </div>
            {d.dre.budget?.partial ? (
              <p className="small muted">
                Realizado até {dateBr(d.dre.budget.actualThrough)} comparado ao orçamento do mês inteiro.
              </p>
            ) : null}
          </>
        ) : (
          <Empty>{MSG.errors.noReportAccess}</Empty>
        )}
      </Section>
    );
    const health = (
      <Section key="health" title="Saúde financeira" icon={HeartPulse}>
        <HealthPanel health={d.health} />
      </Section>
    );
    const flow = (
      <Section key="flow" title="Fluxo de veículos" icon={Car}>
        <div className="grid" data-testid="flow-block">
          <Kpi variant="small" label="No dia" value={qty(d.flow.todayAccess)} />
          <Kpi variant="small" label="Na semana" value={qty(d.flow.weekAccess)} />
          <Kpi
            variant="small"
            label="No mês"
            value={qty(d.flow.monthAccess)}
            sub={
              <>
                <MoM /> <Change c={d.flow.mom} />
              </>
            }
          />
          <Kpi
            variant="small"
            label="Acumulado no ano"
            value={qty(d.flow.ytdAccess)}
            to={can('report.read') ? '/relatorios/comparativos' : undefined}
            drillLabel="Fluxo mês a mês"
          />
          <Kpi variant="small" label="Pagantes no mês" value={qty(d.flow.monthPaying)} />
        </div>
      </Section>
    );
    const comparatives = (
      <Section key="comparatives" title="Comparativos de receita" icon={TrendingUp}>
        <div className="grid">
          <Kpi variant="small" label="Semana" value={brl(d.revenue.weekCents)} />
          <Kpi
            variant="small"
            label="Mês anterior (completo)"
            value={brl(d.revenue.previousMonthFullCents)}
          />
          <Kpi
            variant="small"
            label="Mesmo mês do ano anterior"
            value={brl(d.revenue.sameMonthLastYearCents)}
            sub={
              <>
                <YoY /> <Change c={d.revenue.yoy} />
              </>
            }
          />
          <Kpi
            variant="small"
            label="Acumulado no ano"
            value={brl(d.revenue.ytdCents)}
            sub={
              <>
                <YTD /> <Change c={d.revenue.ytd} />
              </>
            }
            to={can('report.read') ? '/relatorios/comparativos' : undefined}
            drillLabel="Comparativos"
          />
          {d.basis === 'approved' ? (
            <Kpi
              variant="small"
              label="Ainda não aprovado no mês"
              value={brl(d.revenue.provisionalMonthCents - d.revenue.monthCents)}
              sub="dias fechados ou conferidos, fora do total acima"
            />
          ) : null}
        </div>
      </Section>
    );
    const revenueChart = can('report.read') ? (
      <section className="card" key="revenue-chart">
        <h3 className="card-title">
          <TrendingUp size={18} aria-hidden="true" />
          Evolução da receita no mês
        </h3>
        {monthly.isPending && !monthly.error ? <Loading /> : null}
        {monthly.error ? (
          <ErrorBox error={errorMessage(monthly.error)} retry={() => void monthly.refetch()} />
        ) : null}
        {shown.length ? (
          <>
            <Bars
              label={`Receita diária de ${month}`}
              format={(n) => brl(n)}
              axisFormat={axisBrl}
              data={days.map((x) => ({
                label: x.operationalDate.slice(8, 10),
                value: inBasis(x) ? x.revenueCents : 0,
                provisional: !isApproved(x),
              }))}
            />
            {legend}
          </>
        ) : !monthly.isPending && !monthly.error ? (
          <Empty>Nenhum dia contabilizado neste mês na base escolhida.</Empty>
        ) : null}
      </section>
    ) : null;
    const flowChart = can('report.read') ? (
      <section className="card" key="flow-chart">
        <h3 className="card-title">
          <Car size={18} aria-hidden="true" />
          Fluxo de veículos por dia
        </h3>
        {monthly.isPending ? (
          <Loading />
        ) : shown.length ? (
          <>
            <Bars
              label={`Acessos por dia em ${month}`}
              format={qty}
              data={days.map((x) => ({
                label: x.operationalDate.slice(8, 10),
                value: inBasis(x) ? x.accessQty : 0,
                provisional: !isApproved(x),
              }))}
            />
            {legend}
          </>
        ) : (
          <Empty>Nenhum dia contabilizado no período.</Empty>
        )}
      </section>
    ) : null;
    const ticketChart = can('report.read') ? (
      <section className="card" key="ticket-chart">
        <h3 className="card-title">
          <Ticket size={18} aria-hidden="true" />
          Ticket médio por dia
        </h3>
        {monthly.isPending ? (
          <Loading />
        ) : shown.length ? (
          <>
            <Bars
              label={`Ticket médio diário em ${month}`}
              format={(n) => brl(n)}
              axisFormat={axisBrl}
              data={days.map((x) => ({
                label: x.operationalDate.slice(8, 10),
                value: inBasis(x) ? (averageTicketCents(x.revenueCents, x.payingQty) ?? 0) : 0,
                provisional: !isApproved(x),
              }))}
            />
            {legend}
          </>
        ) : (
          <Empty>Nenhum dia contabilizado no período.</Empty>
        )}
      </section>
    ) : null;
    const quality = unitId ? (
      <BlockBoundary key="quality">
        <QualityBlock unitId={unitId} date={d.referenceDate} basis={basis} />
      </BlockBoundary>
    ) : null;

    // The same cards in each perspective, chosen and ordered for its reader (ND-14: no page is duplicated).
    switch (perspective) {
      case 'financeiro':
        return {
          main: true,
          groups: [
            {
              title: 'Caixa, conciliação e resultado',
              cards: [pending, cash, channelsCard, dre, health, comparatives],
            },
            { title: 'Fechamentos e volume', cards: [closings, alerts, flow, revenueChart] },
          ],
        };
      case 'qualidade':
        return {
          main: false,
          groups: [
            { title: 'Qualidade e operação', cards: [quality, pending, closings, alerts] },
            { title: 'Volume do período', cards: [flow, flowChart] },
          ],
        };
      default:
        return {
          main: true,
          groups: [
            {
              title: 'Indicadores complementares',
              cards: [pending, channelsCard, cash, closings, alerts, dre],
            },
            {
              title: 'Análise do período',
              cards: [health, flow, comparatives, revenueChart, flowChart, ticketChart],
            },
          ],
        };
    }
  };

  const mainIndicators = (d: Dashboard) => (
    <>
      <h2 className="section-heading">Indicadores principais</h2>
      <div className="kpi-row" data-tour="painel-indicadores">
        <Kpi
          variant="brand"
          icon={Wallet}
          label="Receita do dia"
          value={brl(d.revenue.todayCents)}
          sub={`${dateBr(d.referenceDate)} · ${d.basis === 'approved' ? 'somente se aprovado' : 'inclui provisório'}`}
          testId="kpi-revenue-today"
          to={can('report.read') ? `/relatorios/diario?data=${d.referenceDate}` : undefined}
          drillLabel="Resultado do dia"
        />
        <Kpi
          icon={TrendingUp}
          label="Receita do mês"
          value={brl(d.revenue.monthCents)}
          sub={
            <>
              <MoM /> <Change c={d.revenue.mom} />
            </>
          }
          testId="kpi-revenue-month"
          to={can('report.read') ? `/relatorios/mensal?mes=${month}` : undefined}
          drillLabel="Dias do mês"
        />
        <Kpi
          icon={Car}
          label="Fluxo de veículos (mês)"
          value={qty(d.flow.monthAccess)}
          sub={
            <>
              no dia {qty(d.flow.todayAccess)} · <MoM /> <Change c={d.flow.mom} />
            </>
          }
          to={can('report.read') ? `/relatorios/mensal?mes=${month}` : undefined}
          drillLabel="Acessos por dia"
        />
        <Kpi
          icon={Ticket}
          label="Ticket médio (mês)"
          value={brl(d.ticket.monthCents)}
          sub={`no dia ${brl(d.ticket.dayCents)} · na semana ${brl(d.ticket.weekCents)}`}
          to={can('report.read') ? '/relatorios/receitas' : undefined}
          drillLabel="Receitas por canal"
        />
        {d.reconciliation ? (
          <>
            <Kpi
              icon={Scale}
              label="Conciliado (por valor)"
              value={ratioPct(d.reconciliation.ratioByValue)}
              sub={`${brl(d.reconciliation.allocatedCents)} de ${brl(d.reconciliation.expectedCents)}`}
              testId="kpi-reconciled"
              to="/conciliacao"
              drillLabel="Abrir conciliação"
            />
            <Kpi
              icon={TriangleAlert}
              label="Divergências pendentes"
              value={qty(d.reconciliation.divergentItems)}
              sub={`${brl(d.reconciliation.divergentCents)} divergente · ${qty(d.reconciliation.pendingItems)} itens a conciliar`}
              testId="kpi-divergences"
              to="/excecoes"
              drillLabel="Ver divergências"
            />
          </>
        ) : null}
        {d.divergenceHandling ? (
          <Kpi
            icon={Timer}
            label="Tempo médio de tratamento de divergência"
            value={hoursBr(d.divergenceHandling.averageHours)}
            sub={
              <span title={d.divergenceHandling.definition}>
                {qty(d.divergenceHandling.resolvedCount)} resolvida(s) no mês ·{' '}
                {qty(d.divergenceHandling.openCount)} em aberto · <DemoBadge />
              </span>
            }
            testId="kpi-divergence-time"
            to={can('report.read') ? '/relatorios/excecoes' : '/excecoes'}
            drillLabel="Exceções e justificativas"
          />
        ) : null}
      </div>
    </>
  );

  const layout = d ? body(d) : null;

  return (
    <div>
      <div className="topbar">
        <PageTitle>Painel executivo</PageTitle>
        {panelData ? (
          <div className="actions" data-tour="painel-filtros">
            <DateInputBr
              label="Data de referência"
              hideLabel
              value={date}
              onChange={(iso) => iso && setDate(iso)}
            />
            <select
              value={basis}
              onChange={(e) => setBasis(e.target.value as 'approved' | 'provisional')}
              aria-label="Base dos números"
            >
              <option value="approved">Somente dias aprovados</option>
              <option value="provisional">Aprovados e provisórios</option>
            </select>
          </div>
        ) : null}
      </div>
      {perspective === null ? (
        <Empty>Seu perfil não tem acesso ao painel nesta unidade.</Empty>
      ) : (
        <>
          <PerspectiveTabs
            allowed={allowed}
            current={perspective}
            defaultId={defaultId}
            onSelect={selectPerspective}
          />
          <div
            role={allowed.length > 1 ? 'tabpanel' : undefined}
            id={panelId}
            aria-labelledby={allowed.length > 1 ? tabId(perspective) : undefined}
            data-testid="dashboard-perspective"
            data-perspective={perspective}
            data-tour="painel-conteudo"
          >
            {perspective === 'sistema' ? (
              <>
                <BlockBoundary what="Verificando o sistema">
                  <SystemBlock />
                </BlockBoundary>
                {pending ? (
                  <>
                    <h2 className="section-heading">Pendências do seu perfil</h2>
                    <div className="grid-2 grid-pairs">{pending}</div>
                  </>
                ) : null}
              </>
            ) : (
              <>
                {q.isLoading ? <Loading what="Carregando indicadores" /> : null}
                {q.error ? <ErrorBox error={errorMessage(q.error)} retry={() => void q.refetch()} /> : null}
                {d && layout ? (
                  <>
                    <p className="page-intro" data-testid="dashboard-meta" data-tour="painel-base">
                      Referência {dateBr(d.referenceDate)} · base:{' '}
                      <strong>
                        {d.basis === 'approved' ? 'dias aprovados' : 'dias aprovados e provisórios'}
                      </strong>{' '}
                      · {d.coverage.monthCountedDays} de {Number(d.referenceDate.slice(8, 10))} dias do mês
                      contabilizados · atualizado em {dateTimeBr(d.generatedAt)} <DemoBadge />
                    </p>
                    <p className="small muted" data-testid="dashboard-draft-notice">
                      Rascunhos de movimento, inclusive os importados de arquivo, não entram no painel. O dia
                      passa a contar depois do fechamento (na base com provisórios) ou da aprovação.
                    </p>
                    {layout.main ? mainIndicators(d) : null}
                    {layout.groups.map((g, i) => (
                      <Fragment key={g.title}>
                        <h2 className="section-heading" ref={i === 0 ? secondaryRef : undefined}>
                          {g.title}
                        </h2>
                        <div className="grid-2 grid-pairs">{g.cards}</div>
                      </Fragment>
                    ))}
                  </>
                ) : null}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
